import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { LOAN_TYPES, inputCls, labelCls, btnPrimary } from "@/lib/constants";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, CheckCircle2, Handshake, Loader2, Upload, FileCheck2,
  User, MapPin, CreditCard, Package, FileText, LogIn,
} from "lucide-react";
import { LogoMark } from "@/components/Logo";

const BUSINESS_TYPES = ["Individual", "Proprietorship", "Partnership Firm", "Private Limited", "LLP"];
const DOC_SLOTS = [
  { key: "PAN Card", required: true },
  { key: "Aadhaar Card", required: true },
  { key: "Photograph", required: true },
  { key: "Cancelled Cheque / Passbook", required: true },
  { key: "GST Certificate", required: false },
  { key: "Address Proof", required: false },
];

const STEPS = [
  { id: 0, label: "Basic Details", icon: User },
  { id: 1, label: "Address", icon: MapPin },
  { id: 2, label: "KYC & Bank", icon: CreditCard },
  { id: 3, label: "Products", icon: Package },
  { id: 4, label: "Documents", icon: FileText },
];

const emptyForm = {
  name: "", firm_name: "", email: "", phone: "", business_type: "Individual", partner_type: "dsa", dob: "", gender: "",
  address: "", city: "", state: "", pincode: "",
  pan: "", aadhaar: "", gst: "", account_holder: "", bank_name: "", account_number: "", ifsc: "",
  products: [], experience: "",
};

export default function PartnerSignup() {
  const { token } = useParams();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [docs, setDocs] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [partnerTypes, setPartnerTypes] = useState([]);

  useEffect(() => {
    api.get("/public/partner-types")
      .then((r) => {
        setPartnerTypes(r.data);
        if (r.data.length) setForm((f) => ({ ...f, partner_type: r.data[0].key }));
      })
      .catch(() => setPartnerTypes([]));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleProduct = (p) =>
    setForm((f) => ({ ...f, products: f.products.includes(p) ? f.products.filter((x) => x !== p) : [...f.products, p] }));

  const onFile = (slotKey, e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File too large (max 5MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setDocs((d) => ({ ...d, [slotKey]: { doc_type: slotKey, file_name: file.name, data: reader.result } }));
    reader.readAsDataURL(file);
  };

  const validateStep = () => {
    if (step === 0) {
      if (!form.name || !form.phone) { toast.error("Name and phone are required"); return false; }
    }
    return true;
  };

  const next = () => { if (validateStep()) setStep((s) => Math.min(s + 1, STEPS.length - 1)); };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async (e) => {
    e.preventDefault();
    const missing = DOC_SLOTS.filter((d) => d.required && !docs[d.key]);
    if (missing.length) { toast.error(`Please upload: ${missing.map((m) => m.key).join(", ")}`); return; }
    setLoading(true);
    try {
      const payload = { ...form, referral_code: token || "", documents: Object.values(docs) };
      const { data } = await api.post("/public/partner-signup", payload);
      setResult(data);
      toast.success("Registration submitted!");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (result) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div data-testid="signup-success" className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-slate-100 p-8 text-center animate-in">
        <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto"><CheckCircle2 size={34} /></div>
        <h2 className="text-2xl font-extrabold text-slate-900 font-heading mt-4">Registration Submitted!</h2>
        <p className="text-sm text-slate-500 mt-2">Thank you for registering as a partner with Arjun Fintech. Our onboarding team will verify your KYC and share the agreement.</p>
        <div className="mt-5 p-4 rounded-xl bg-slate-50 border border-slate-200">
          <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Your Reference ID</div>
          <div className="text-xl font-bold text-emerald-600 font-mono mt-1">{result.reference}</div>
          <div className="text-xs text-slate-400 mt-1">{result.documents_saved} document(s) saved</div>
        </div>
        <Link to="/" className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-emerald-600 hover:text-emerald-700">
          <ArrowLeft size={15} /> Back to Home
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <LogoMark className="w-9 h-9" />
            <div>
              <div className="text-white font-bold font-heading leading-tight text-sm">Arjun Fintech LLP</div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-mono">Partner Onboarding</div>
            </div>
          </Link>
          <Link to="/login" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white font-medium text-sm transition-all border border-white/10">
            <LogIn size={16} /> Login
          </Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium">
            <Handshake size={13} /> Become a Channel Partner
          </span>
          <h1 className="mt-3 text-2xl sm:text-3xl font-extrabold text-slate-900 font-heading tracking-tight">Partner Registration</h1>
          <p className="text-sm text-slate-500 mt-1">Complete your details and upload KYC documents to join the Arjun Fintech network.{token ? ` (Referral: ${token.slice(0, 8).toUpperCase()})` : ""}</p>
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-1 sm:gap-2 mb-8 overflow-x-auto pb-2">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex items-center flex-shrink-0">
              <button data-testid={`step-${s.id}`} onClick={() => i < step && setStep(i)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${step === i ? "bg-slate-900 text-white" : step > i ? "bg-emerald-100 text-emerald-700" : "bg-white text-slate-400 border border-slate-200"}`}>
                {step > i ? <CheckCircle2 size={15} /> : <s.icon size={15} />}
                <span className="hidden sm:inline">{s.label}</span>
              </button>
              {i < STEPS.length - 1 && <div className={`w-4 sm:w-8 h-0.5 ${step > i ? "bg-emerald-400" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8">
          {step === 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-in">
              <div className="sm:col-span-2"><label className={labelCls}>Full Name *</label><input data-testid="signup-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Firm Name</label><input data-testid="signup-firm-name" value={form.firm_name} onChange={(e) => set("firm_name", e.target.value)} className={inputCls} placeholder="Registered firm / company name (if any)" /></div>
              <div><label className={labelCls}>Mobile *</label><input data-testid="signup-phone" required value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} placeholder="10-digit mobile" /></div>
              <div><label className={labelCls}>Email</label><input data-testid="signup-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Business Type</label>
                <select data-testid="signup-business-type" value={form.business_type} onChange={(e) => set("business_type", e.target.value)} className={inputCls}>
                  {BUSINESS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div><label className={labelCls}>Partner Type</label>
                <select data-testid="signup-partner-type" value={form.partner_type} onChange={(e) => set("partner_type", e.target.value)} className={inputCls}>
                  {partnerTypes.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </div>
              <div><label className={labelCls}>Date of Birth</label><input data-testid="signup-dob" type="date" value={form.dob} onChange={(e) => set("dob", e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Gender</label>
                <select data-testid="signup-gender" value={form.gender} onChange={(e) => set("gender", e.target.value)} className={inputCls}>
                  <option value="">Select…</option><option>Male</option><option>Female</option><option>Other</option>
                </select>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-in">
              <div className="sm:col-span-2"><label className={labelCls}>Address</label><textarea data-testid="signup-address" rows={2} value={form.address} onChange={(e) => set("address", e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>City</label><input data-testid="signup-city" value={form.city} onChange={(e) => set("city", e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>State</label><input data-testid="signup-state" value={form.state} onChange={(e) => set("state", e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Pincode</label><input data-testid="signup-pincode" value={form.pincode} onChange={(e) => set("pincode", e.target.value)} className={inputCls} /></div>
            </div>
          )}

          {step === 2 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-in">
              <div className="sm:col-span-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Identity (KYC)</div>
              <div><label className={labelCls}>PAN Number</label><input data-testid="signup-pan" value={form.pan} onChange={(e) => set("pan", e.target.value.toUpperCase())} className={inputCls} placeholder="ABCPX1234K" /></div>
              <div><label className={labelCls}>Aadhaar Number</label><input data-testid="signup-aadhaar" value={form.aadhaar} onChange={(e) => set("aadhaar", e.target.value)} className={inputCls} placeholder="12-digit" /></div>
              <div><label className={labelCls}>GST Number (optional)</label><input data-testid="signup-gst" value={form.gst} onChange={(e) => set("gst", e.target.value.toUpperCase())} className={inputCls} /></div>
              <div className="sm:col-span-2 text-xs font-semibold uppercase tracking-wider text-slate-400 border-t border-slate-100 pt-4 mt-2">Bank Account (for payouts)</div>
              <div><label className={labelCls}>Account Holder Name</label><input data-testid="signup-holder" value={form.account_holder} onChange={(e) => set("account_holder", e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Bank Name</label><input data-testid="signup-bank" value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Account Number</label><input data-testid="signup-account" value={form.account_number} onChange={(e) => set("account_number", e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>IFSC Code</label><input data-testid="signup-ifsc" value={form.ifsc} onChange={(e) => set("ifsc", e.target.value.toUpperCase())} className={inputCls} /></div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5 animate-in">
              <div>
                <label className={labelCls}>Loan Products You Want to Source</label>
                <div className="flex flex-wrap gap-2">
                  {LOAN_TYPES.map((p) => (
                    <button type="button" key={p} data-testid={`signup-product-${p.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => toggleProduct(p)}
                      className={`text-sm px-4 py-2 rounded-full border transition-colors ${form.products.includes(p) ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}>{p}</button>
                  ))}
                </div>
              </div>
              <div className="sm:w-1/2">
                <label className={labelCls}>Experience in Loan Sourcing</label>
                <select data-testid="signup-experience" value={form.experience} onChange={(e) => set("experience", e.target.value)} className={inputCls}>
                  <option value="">Select…</option><option>Fresher</option><option>Less than 1 year</option><option>1-3 years</option><option>3-5 years</option><option>5+ years</option>
                </select>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4 animate-in">
              <p className="text-sm text-slate-500">Upload clear scans/photos (JPG, PNG or PDF, max 5MB each). Documents marked * are required.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {DOC_SLOTS.map((slot) => {
                  const uploaded = docs[slot.key];
                  return (
                    <label key={slot.key} data-testid={`doc-slot-${slot.key.replace(/[^a-z]+/gi, "-").toLowerCase()}`}
                      className={`flex items-center gap-3 p-4 rounded-xl border-2 border-dashed cursor-pointer transition-all ${uploaded ? "border-emerald-400 bg-emerald-50" : "border-slate-300 hover:border-slate-400 bg-slate-50"}`}>
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${uploaded ? "bg-emerald-100 text-emerald-600" : "bg-white text-slate-400 border border-slate-200"}`}>
                        {uploaded ? <FileCheck2 size={20} /> : <Upload size={20} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-800">{slot.key} {slot.required && <span className="text-rose-500">*</span>}</div>
                        <div className="text-xs text-slate-400 truncate">{uploaded ? uploaded.file_name : "Click to upload"}</div>
                      </div>
                      <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => onFile(slot.key, e)} />
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Nav buttons */}
          <div className="flex items-center justify-between gap-3 mt-8 pt-6 border-t border-slate-100">
            <button type="button" onClick={back} disabled={step === 0}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed">
              <ArrowLeft size={16} /> Back
            </button>
            {step < STEPS.length - 1 ? (
              <button type="button" data-testid="signup-next-button" onClick={next} className={btnPrimary}>Continue <ArrowRight size={16} /></button>
            ) : (
              <button type="submit" data-testid="signup-submit-button" disabled={loading} className={btnPrimary}>
                {loading ? <Loader2 size={18} className="animate-spin" /> : <>Submit Registration <CheckCircle2 size={16} /></>}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
